import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSchoolYear } from "../../context/SchoolYearContext";
import { motion } from "framer-motion";
import { useCollections } from "../../hooks/useData";
import { formatPLN, formatDate, statusColor } from "../../lib/finance";

function SectionCard({ title, subtitle, children, delay = 0, action = null }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      className="rounded-[30px] border border-white/70 bg-white/78 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-xl font-semibold tracking-tight text-slate-900">
            {title}
          </div>
          {subtitle && (
            <div className="mt-1 text-sm leading-6 text-slate-500">
              {subtitle}
            </div>
          )}
        </div>
        {action}
      </div>

      {children}
    </motion.section>
  );
}

function CollectionCard({ col, index }) {
  const { isAdmin } = useAuth();
  const { isSelectedYearActive, isSelectedYearDraft } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);

  const progress = Math.min(
    100,
    (Number(col.current_amount ?? 0) / Number(col.target_amount ?? 1)) * 100
  );

  const missing = Math.max(
    0,
    Number(col.target_amount ?? 0) - Number(col.current_amount ?? 0)
  );

  const isActive = col.status === "active" || col.status === "aktywna";
  const isFinished =
    col.status === "zakończona" ||
    col.status === "zamknięta" ||
    col.status === "closed";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.4 }}
      className="overflow-hidden rounded-[28px] border border-white/70 bg-white/82 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.05)]"
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                {col.name}
              </h3>

              {isActive && (
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Aktywna
                </span>
              )}

              {isFinished && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  Zakończona
                </span>
              )}
            </div>

            {col.description && (
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
                {col.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 self-start">
            <span className={`badge ${statusColor(col.status)}`}>{col.status}</span>

            {canWrite && (
              <Link
                to={`/collection/edit/${col.id}`}
                className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
              >
                Edytuj
              </Link>
            )}
          </div>
        </div>

        <div className="rounded-[24px] bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 p-5">
          <div className="mb-3 flex items-center justify-between gap-3 text-sm">
            <span className="text-slate-500">Zebrano</span>
            <span className="font-medium text-slate-800">
              {formatPLN(col.current_amount)} / {formatPLN(col.target_amount)}
            </span>
          </div>

          <div className="h-3 overflow-hidden rounded-full bg-white/80">
            <motion.div
              className={`h-full rounded-full ${
                isActive
                  ? "bg-gradient-to-r from-emerald-400 via-teal-400 to-amber-300"
                  : "bg-slate-300"
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{
                delay: 0.25 + index * 0.06,
                duration: 0.85,
                ease: "easeOut",
              }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{progress.toFixed(1)}%</span>
            {missing > 0 ? (
              <span>
                Brakuje <span className="font-medium text-slate-700">{formatPLN(missing)}</span>
              </span>
            ) : (
              <span className="font-semibold text-emerald-700">Cel osiągnięty</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Cel
            </div>
            <div className="mt-2 text-lg font-semibold text-slate-900">
              {formatPLN(col.target_amount)}
            </div>
          </div>

          <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Zebrano
            </div>
            <div className="mt-2 text-lg font-semibold text-emerald-700">
              {formatPLN(col.current_amount)}
            </div>
          </div>

          <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Utworzono
            </div>
            <div className="mt-2 text-sm font-medium text-slate-700">
              {formatDate(col.created_at)}
            </div>
          </div>
        </div>

        {col.notes && (
          <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 px-4 py-4 text-sm leading-6 text-slate-500">
            {col.notes}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function CollectionScreen() {
  const { data: collections = [], loading } = useCollections();
  const { isAdmin } = useAuth();
  const { isSelectedYearActive, isSelectedYearDraft, isSelectedYearArchived } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const active = collections.filter(
    (c) => c.status === "active" || c.status === "aktywna"
  );

  const history = collections.filter(
    (c) =>
      c.status === "zakończona" ||
      c.status === "zamknięta" ||
      c.status === "closed"
  );

  const other = collections.filter(
    (c) =>
      c.status !== "active" &&
      c.status !== "aktywna" &&
      c.status !== "zakończona" &&
      c.status !== "zamknięta" &&
      c.status !== "closed"
  );

  return (
    <div className="space-y-6 lg:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.82)_0%,rgba(236,253,245,0.72)_45%,rgba(255,247,237,0.76)_100%)] px-6 py-7 shadow-[0_18px_52px_rgba(15,23,42,0.06)]"
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
            Finanse
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Zbiórki
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Podgląd aktywnych i zakończonych zbiórek wraz z postępem oraz historią wpłat.
          </p>

          {canWrite && (
            <div className="mt-6">
              <Link
                to="/admin?tab=collection"
                className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.28)] transition-transform duration-200 hover:-translate-y-0.5"
              >
                Utwórz nową zbiórkę
              </Link>
            </div>
          )}

          {isAdmin && isSelectedYearDraft && (
            <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
              Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
            </div>
          )}

          {isAdmin && isSelectedYearArchived && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              Rok tylko do odczytu. Tworzenie i edycja zbiórek są wyłączone.
            </div>
          )}
        </div>
      </motion.div>

      {active.length === 0 && (
        <SectionCard
          title="Brak aktywnej zbiórki"
          subtitle="Na ten moment nie ma zbiórki oznaczonej jako aktywna."
          delay={0.08}
          action={
            history.length > 0 ? (
              <a
                href="#historia"
                className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-slate-50"
              >
                Przejdź do historii
              </a>
            ) : null
          }
        >
          <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500">
            Brak aktywnej zbiórki do wyświetlenia.
          </div>
        </SectionCard>
      )}

      {active.length > 0 && (
        <SectionCard
          title="Aktywne"
          subtitle={`Obecnie aktywne zbiórki: ${active.length}`}
          delay={0.1}
        >
          <div className="space-y-4">
            {active.map((col, i) => (
              <CollectionCard key={col.id} col={col} index={i} />
            ))}
          </div>
        </SectionCard>
      )}

      {history.length > 0 && (
        <SectionCard
          title="Historia zbiórek"
          subtitle="Zakończone i zamknięte zbiórki"
          delay={0.16}
          className=""
        >
          <div id="historia" className="space-y-4">
            {history.map((col, i) => (
              <CollectionCard key={col.id} col={col} index={i} />
            ))}
          </div>
        </SectionCard>
      )}

      {other.length > 0 && (
        <SectionCard
          title="Pozostałe"
          subtitle="Zbiórki o innych statusach"
          delay={0.22}
        >
          <div className="space-y-4">
            {other.map((col, i) => (
              <CollectionCard key={col.id} col={col} index={i} />
            ))}
          </div>
        </SectionCard>
      )}

      {collections.length === 0 && (
        <SectionCard
          title="Brak zbiórek"
          subtitle="Nie ma jeszcze żadnych zapisanych zbiórek."
          delay={0.08}
        >
          <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500">
            Brak zbiórek do wyświetlenia.
          </div>
        </SectionCard>
      )}
    </div>
  );
}
